import { getChildrenItems } from '../services/getChildrenItems';

// Check if itemA is an ancestor of itemB (prevents circular references)
export async function isAncestor(itemA, itemB) {
    if (!itemA || !itemB) return false;
    
    try {
        let currentItem = itemB;
        const visited = new Set();
        
        while (currentItem) {
            if (visited.has(currentItem)) {
                // Circular reference detected
                return true;
            }
            
            if (currentItem === itemA) {
                return true;
            }
            
            visited.add(currentItem);
            
            // Get parent of current item
            const children = await getChildrenItems(currentItem);
            const itemData = children.find(child => child._id === currentItem);
            
            if (!itemData || !itemData.ed) {
                break;
            }
            
            currentItem = itemData.ed;
        }
        
        return false;
    } catch (error) {
        console.error("Error checking ancestor relationship:", error);
        return true; // Err on the side of caution
    }
}

// Transform children data for ImageIconGrid
export function transformToIconData(childItems) {
    return childItems.map(child => ({
        url: child.icon || (child.isFolder ? '/default-folder-icon.png' : '/default-file-icon.png'),
        text: child.title || (child.firstname && child.name ? `${child.firstname} ${child.name}` : 'Untitled'),
        target: {
            _id: child._id,
            di: child.di,
            ed: child.ed,
            pa: child.pa,
            isFolder: child.isFolder,
            itemData: child
        }
    }));
}

// Transform children data for tree view
export function transformToTreeData(childItems) {
    return childItems.map(child => ({
        id: child._id,
        icon: child.isFolder ? 'fa-folder' : 'fa-file',
        text: child.title || (child.firstname && child.name ? `${child.firstname} ${child.name}` : 'Untitled'),
        isFolder: child.isFolder,
        itemData: child
    }));
}

// Calculate effective root ID
export function calculateEffectiveRootId(rootId, qwantaContext, currentUser) {
    if (rootId === "self" && qwantaContext?._id) {
        return qwantaContext._id;
    }
    return !rootId ? currentUser?.id : rootId;
}

// Create drag payload
export function createDragPayload(itemId, isFolder, displayText, localItemData) {
    return {
        items: [{
            itemId,
            isFolder,
            text: displayText,
            index: itemId,
            ed: localItemData?.ed || itemId,
            di: localItemData?.di || null,
            pa: localItemData?.pa || null,
            data: localItemData
        }]
    };
}

// Check if drop is on specific element
export function isDropOnElement(e, elementRef) {
    if (!elementRef.current) return false;

    const rect = elementRef.current.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    return (
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
    );
}

// Get selection styles for components
export function getSelectionStyle(isSelected, isHovering, enabled = true) {
    if (!enabled) return {};

    if (isSelected) {
        return {
            outline: '1px dashed #2196f3',
            outlineOffset: '2px'
        };
    }
    if (isHovering) {
        return {
            outline: '1px dashed #ff9800',
            outlineOffset: '1px'
        };
    }
    return {};
}

// Get background color based on state
export function getBackgroundColor(isSelected, isHovering) {
    if (isSelected) {
        return isHovering ? '#c6e6ff' : '#e6f7ff';
    }
    return isHovering ? '#f0f7ff' : 'transparent';
}

// Get border style based on state
export function getBorderStyle(isHovering, isFolder) {
    if (isHovering && isFolder) {
        return '2px dashed #1890ff';
    } else if (isHovering) {
        return '2px dashed #40a9ff';
    }
    return '1px solid #eee';
}

// Create card data for current card updates
export function createCardData(itemData, itemId, displayText) {
    return {
        _id: itemData?._id || itemId,
        di: itemData?.di || null,
        ed: itemData?.ed || itemId,
        pa: itemData?.pa || null,
        title: displayText || itemData?.title,
        brief: itemData?.brief,
        isFolder: itemData?.isFolder,
        _forceUpdate: Date.now()
    };
}